import React, { useState, useEffect, useRef } from "react";
import "./Portfolio.css";

// Components
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import About from "./components/About";
import Experience from "./components/Experience";
import Skills from "./components/Skills";
import Projects from "./components/Projects";
import ProjectModal from "./components/ProjectModal";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import ThankYouModal from "./components/ThankYouModal";

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState("home");
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [isLightTheme, setIsLightTheme] = useState(() => {
    return localStorage.getItem("portfolio-theme") === "light";
  });

  useEffect(() => {
    document.documentElement.dataset.theme = isLightTheme ? "light" : "dark";
    localStorage.setItem("portfolio-theme", isLightTheme ? "light" : "dark");
  }, [isLightTheme]);

  const toggleTheme = () => setIsLightTheme((currentTheme) => !currentTheme);

  // Refs for scroll animations
  const aboutRef = useRef(null);
  const experienceRef = useRef(null);
  const skillsRef = useRef(null);
  const projectsRef = useRef(null);
  const contactRef = useRef(null);

  // Initialize EmailJS
  useEffect(() => {
    if (window.emailjs) {
      window.emailjs.init("Nd9jiARGjVtarMcnm");
    }
  }, []);

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setActiveSection(sectionId);
      setIsMenuOpen(false);
    }
  };

  // Scroll effect for navbar and animations
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      // Update scrolled state for navbar
      setScrolled(scrollPosition > 50);

      // Handle section active states
      const sections = [
        "home",
        "about",
        "experience",
        "skills",
        "projects",
        "contact",
      ];
      const scrollPos = scrollPosition + 100;

      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;

          if (scrollPos >= offsetTop && scrollPos < offsetTop + height) {
            setActiveSection(section);
          }
        }
      });

      // Animate sections on scroll with dynamic staggered children
      const animateOnScroll = (ref, className, childSelector) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight * 0.95;

          if (isVisible && !ref.current.classList.contains(className)) {
            ref.current.classList.add(className);

            // Apply staggered animation to children
            if (childSelector) {
              const children = ref.current.querySelectorAll(childSelector);
              children.forEach((child, index) => {
                setTimeout(() => {
                  child.classList.add("fade-in");
                  child.style.animationDelay = `${(index % 4) * 0.1}s`;
                }, 0);
              });
            }
          }
        }
      };

      animateOnScroll(aboutRef, "animate-in", ".stat");
      animateOnScroll(experienceRef, "animate-in", ".experience-item");
      animateOnScroll(skillsRef, "animate-in", ".skill-item");
      animateOnScroll(projectsRef, "animate-in", ".project-card");
      animateOnScroll(contactRef, "animate-in");
    };

    window.addEventListener("scroll", handleScroll);
    // Run once on mount so any in-view sections are already visible
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="portfolio">
      <Navbar
        scrolled={scrolled}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        activeSection={activeSection}
        scrollToSection={scrollToSection}
        isLightTheme={isLightTheme}
        toggleTheme={toggleTheme}
      />

      <Hero scrollToSection={scrollToSection} />
      <About ref={aboutRef} />
      <Experience ref={experienceRef} />
      <Skills ref={skillsRef} />
      <Projects
        ref={projectsRef}
        setSelectedProject={setSelectedProject}
        setCurrentImageIndex={setCurrentImageIndex}
      />
      <Contact ref={contactRef} setShowThankYouModal={setShowThankYouModal} />

      <ProjectModal
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        currentImageIndex={currentImageIndex}
        setCurrentImageIndex={setCurrentImageIndex}
      />

      <ThankYouModal
        showThankYouModal={showThankYouModal}
        setShowThankYouModal={setShowThankYouModal}
      />

      <Footer />
    </div>
  );
};

export default Portfolio;
